import { WeaponMode } from '../weapons/WeaponSystem.js';

const FLAP_LABELS = ['UP', 'HALF', 'FULL'];
const HOLD_MS = 3000; // visible duration before the badge fades out

/**
 * Transient command-feedback badge shown at bottom-center while playing.
 *
 * Polls the handful of player-commandable states (throttle, flaps, gear,
 * selected weapon) once per frame and, on any change, flashes a small orange
 * text badge — e.g. "THROTTLE: 50%" — that holds for {@link HOLD_MS} then fades
 * out (CSS-driven). A new change interrupts the current badge: its text is
 * replaced and the hold timer restarts.
 *
 * Pure change-detection means no wiring into the input layer, so it reports
 * commands from any source (keyboard, mouse, programmatic) and adds only a few
 * comparisons per frame. Throttle is read from the controller (the commanded
 * input) so the spawn-time level-flight setting seeds cleanly without flashing.
 */
export default class CommandFeedback {
  constructor({ app, controller, aircraft, weaponSystem }) {
    this.app = app;
    this.controller = controller;
    this.aircraft = aircraft;
    this.weaponSystem = weaponSystem;

    const el = document.createElement('div');
    el.id = 'command-feedback';
    this.el = el;
    document.getElementById('app').appendChild(el);

    // Seed last-seen values so the spawn state doesn't flash a badge on frame 1.
    this._lastThrottle = this._throttlePct();
    this._lastFlap = aircraft.flapStage;
    this._lastGear = aircraft.gearTarget;
    this._lastWeapon = weaponSystem ? weaponSystem.mode : WeaponMode.NONE;
    this._lastNvg = app ? !!app.nvgOn : false;
    // Seeded on the first update(): the game resumes after this constructor
    // runs, so reading app.paused here would flash a spurious "RESUMED".
    this._lastPaused = null;

    this._hideTimer = 0;
    this._enabled = true;
  }

  setVisible(v) {
    this._enabled = v;
    this.el.style.display = v ? '' : 'none';
    if (!v) { clearTimeout(this._hideTimer); this.el.classList.remove('is-visible'); }
  }

  update() {
    const a = this.aircraft;

    const throttle = this._throttlePct();
    if (throttle !== this._lastThrottle) {
      this._lastThrottle = throttle;
      this._show(`THROTTLE: ${throttle}%`);
    }

    if (a.flapStage !== this._lastFlap) {
      this._lastFlap = a.flapStage;
      this._show(`FLAPS: ${FLAP_LABELS[a.flapStage]}`);
    }

    // gearTarget: 1 = down, 0 = up.
    if (a.gearTarget !== this._lastGear) {
      this._lastGear = a.gearTarget;
      this._show(`GEAR: ${a.gearTarget ? 'DOWN' : 'UP'}`);
    }

    const weapon = this.weaponSystem ? this.weaponSystem.mode : WeaponMode.NONE;
    if (weapon !== this._lastWeapon) {
      this._lastWeapon = weapon;
      this._show(`WEAPON: ${this.weaponSystem.modeLabel}`);
    }

    const nvg = this.app ? !!this.app.nvgOn : false;
    if (nvg !== this._lastNvg) {
      this._lastNvg = nvg;
      this._show(`NVG: ${nvg ? 'ON' : 'OFF'}`);
    }

    // In-place pause (P key) keeps the playing state, so flash a badge to make
    // the otherwise-silent freeze obvious.
    const paused = this.app ? this.app.paused : false;
    if (this._lastPaused === null) {
      this._lastPaused = paused; // seed baseline without flashing
    } else if (paused !== this._lastPaused) {
      this._lastPaused = paused;
      this._show(paused ? 'GAME PAUSED' : 'RESUMED');
    }
  }

  /** Flash an arbitrary message (device connected, etc.). */
  flash(text) {
    this._show(text);
  }

  _throttlePct() {
    return Math.round(this.controller.throttle * 100);
  }

  _show(text) {
    if (!this._enabled) return;
    this.el.textContent = text;
    this.el.classList.add('is-visible');
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => this.el.classList.remove('is-visible'), HOLD_MS);
  }

  dispose() {
    clearTimeout(this._hideTimer);
    this.el.remove();
  }
}
